import React, { useMemo } from 'react';

// Define a minimal type that works with both Control and Attribute
interface AttributeLike {
    _id: string;
    name?: string;
    description?: string;
    isInformational?: boolean;
    enablingAttribute?: string;
    enablingValue?: any;
}

interface DynamicDescriptionRendererProps {
    /** The name of the field this description is for */
    fieldName: string;
    /** The current value(s) of the field */
    currentValue: string | string[] | null | undefined;
    /** All attributes from the API to check for matching descriptions */
    allAttributes: AttributeLike[];
}

/**
 * DynamicDescriptionRenderer
 * 
 * Dynamically shows or hides description text based on form field selections.
 * Works with enabling relationships defined in the API response.
 * 
 * Features:
 * - Zero hardcoding - all logic driven by API data
 * - Supports comma-separated enabling values
 * - Case-insensitive matching
 * - Accessible with ARIA attributes
 * - Smooth animations
 */
export const DynamicDescriptionRenderer: React.FC<DynamicDescriptionRendererProps> = ({
    fieldName,
    currentValue,
    allAttributes
}) => {
    // Find all informational attributes that should be shown for this field
    const activeDescriptions = useMemo(() => {
        if (!allAttributes || !fieldName || !currentValue) {
            return [];
        }

        // Normalize current value to array for consistent processing
        const currentValues = Array.isArray(currentValue) ? currentValue : [currentValue];
        const normalizedCurrentValues = currentValues
            .filter(v => v != null && v !== '')
            .map(v => String(v).trim().toLowerCase());

        if (normalizedCurrentValues.length === 0) {
            return [];
        }

        // Find attributes where:
        // 1. isInformational is true
        // 2. enablingAttribute matches the fieldName
        // 3. current value matches one of the enablingValue options
        return allAttributes.filter(attr => {
            // Must be informational
            if (!attr.isInformational) {
                return false;
            }

            // Must have an enabling attribute that matches our field
            if (!attr.enablingAttribute || attr.enablingAttribute.trim() === '') {
                return false;
            }

            // Check if enabling attribute matches (case-insensitive)
            const enablingAttrNormalized = attr.enablingAttribute.trim().toLowerCase();
            const fieldNameNormalized = fieldName.trim().toLowerCase();

            if (enablingAttrNormalized !== fieldNameNormalized) {
                return false;
            }

            // Must have an enabling value
            if (!attr.enablingValue || attr.enablingValue.trim() === '') {
                return false;
            }

            // Parse comma-separated enabling values
            const enablingValues = attr.enablingValue
                .split(',')
                .map((v: string) => v.trim().toLowerCase())
                .filter((v: string) => v !== '');

            // Check if any of the current values match any of the enabling values
            return normalizedCurrentValues.some(currentVal =>
                enablingValues.includes(currentVal)
            );
        });
    }, [fieldName, currentValue, allAttributes]);

    // If no descriptions should be shown, render nothing
    if (activeDescriptions.length === 0) {
        return null;
    }

    return (
        <div className="dynamic-description-container">
            {activeDescriptions.map((attr, index) => (
                <div
                    key={attr._id || index}
                    className="dynamic-description-item"
                    role="alert"
                    aria-live="polite"
                >
                    {attr.description && (
                        <p className="dynamic-description-text pt-2">
                            {attr.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
};

export default DynamicDescriptionRenderer;